import { Task } from '@st/redux'
import type { AuthError, OAuthProvider } from '@st/sdk'
import { Effect } from 'effect'
import { match } from '@st/pattern'
import { Routing, RuntimeConfig, SdkClient, type SdkFailure } from '~/effects'

export const loadPublicFolder = Task.define((folderId: string) =>
  Effect.gen(function* () {
    const sdk = yield* SdkClient
    const publicFolder = yield* sdk.send({
      type: 'folders/getPublicFolderState',
      folderId
    })

    if (!publicFolder || isErrorResponse(publicFolder) || publicFolder.deleted) {
      return { type: 'publicFolderNotFound' } as const
    }
    return { type: 'publicFolderLoaded', publicFolder } as const
  }).pipe(
    Effect.catchTag('HttpStatusError', (error) => {
      if (
        error.response.status == 401 ||
        error.response.status == 403 ||
        error.response.status == 404
      ) {
        return Effect.succeed({ type: 'publicFolderNotFound' } as const)
      }
      return Effect.fail(error)
    }),
    // A network or server failure is not "not found", and there is no
    // load-failed state yet, so it is a defect.
    Effect.orDie
  )
)

export function sendOrganizerVerificationEmail(
  folderId: string,
  userId: string,
  continuePath: string
) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const config = yield* RuntimeConfig

    return yield* verificationEmailResult(
      sdk.send({
        type: 'folders/sendFolderEmailVerificationLink',
        folderId,
        userId,
        continueUrl: absoluteUrl(config.browserOrigin, continuePath)
      }),
      () => ({ type: 'verificationEmailSent' }) as const
    )
  })
}

export function sendLookupVerificationEmail(
  organizationId: string,
  schemaId: string,
  email: string,
  clientKind: 'new' | 'returning',
  continuePath: string
) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const config = yield* RuntimeConfig
    const { taxYear, returnType } = yield* splitSchemaId(schemaId)

    return yield* verificationEmailResult(
      sdk.send({
        type: 'folders/sendLookupFolderEmailVerificationLink',
        organizationId,
        taxYear,
        returnType,
        email,
        clientKind,
        continueUrl: absoluteUrl(config.browserOrigin, continuePath)
      }),
      (response) =>
        match(response)
          .with({ status: 'sent' }, () => ({ type: 'verificationEmailSent' }) as const)
          .with({ status: 'organizer_not_found' }, () => ({
            type: 'lookupOrganizerNotFound' as const,
            email
          }))
          .exhaustive()
    )
  })
}

export const redirectToOAuthLogin = Task.define((provider: OAuthProvider, postLoginPath: string) =>
  Effect.gen(function* () {
    const sdk = yield* SdkClient
    const routing = yield* Routing
    const config = yield* RuntimeConfig

    const response = yield* sdk.send({
      type: 'accounts/generateOAuthLoginUrl',
      aud: provider,
      oauthCallbackUrl: absoluteUrl(config.browserOrigin, `/oauth/callback/${provider}`),
      postLoginRedirectUrl: absoluteUrl(config.browserOrigin, postLoginPath)
    })
    yield* routing.redirectTo(response.url)
    return null
    // No failure has a product response yet, so an unexpected one is a defect.
  }).pipe(Effect.orDie)
)

export function lookupFolders(
  organizationId: string,
  schemaId: string,
  autoCreateWhenNotFound = true
) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const response = yield* sdk.send({
      type: 'folders/lookupFolder',
      organizationId,
      schemaId,
      autoCreateWhenNotFound
    })

    if (!response || isErrorResponse(response)) {
      return { type: 'folderLookupFailed' } as const
    }
    return match(response.status)
      .with('created', () => {
        if (!response.createdFolderId) throw new Error('Created folder response is missing its ID')
        return { type: 'folderCreated', folderId: response.createdFolderId } as const
      })
      .otherwise(() => ({ type: 'foldersFound', folders: response.folders }) as const)
  }).pipe(
    Effect.catchTags({
      TransportError: () => Effect.succeed({ type: 'folderLookupFailed' } as const),
      TimeoutError: () => Effect.succeed({ type: 'folderLookupFailed' } as const)
    }),
    // Any refused lookup is a failed lookup; the SDK client reports server errors.
    Effect.catchTag('HttpStatusError', () =>
      Effect.succeed({ type: 'folderLookupFailed' } as const)
    )
  )
}

function verificationEmailResult<A, B, R>(
  effect: Effect.Effect<A, SdkFailure, R>,
  onSuccess: (response: A) => B
) {
  return effect.pipe(
    Effect.map((response) => {
      if (isErrorResponse(response)) {
        return {
          type: 'verificationEmailFailed',
          error: authErrorFromResponse(response)
        } as const
      }
      return onSuccess(response)
    }),
    Effect.catchTags({
      TransportError: (error) =>
        Effect.succeed({ type: 'verificationEmailFailed', error: toAuthError(error) } as const),
      TimeoutError: (error) =>
        Effect.succeed({ type: 'verificationEmailFailed', error: toAuthError(error) } as const)
    }),
    Effect.catchTag('HttpStatusError', (error) =>
      Effect.succeed({
        type: 'verificationEmailFailed',
        error: {
          type: 'unknown',
          // Any other refusal gets the generic message; the SDK client reports server errors.
          message:
            error.response.status == 401
              ? 'Unauthorized'
              : 'Something went wrong. Please try again.'
        }
      } as const)
    )
  )
}

function splitSchemaId(schemaId: string) {
  const [taxYearString, ...returnTypeParts] = schemaId.split('-')
  const taxYear = Number(taxYearString)
  const returnType = returnTypeParts.join('-')

  if (!Number.isFinite(taxYear) || returnType.length == 0) {
    return Effect.die(new Error(`Invalid schemaId: ${schemaId}`))
  }
  return Effect.succeed({ taxYear, returnType })
}

function absoluteUrl(browserOrigin: string, path: string): string {
  return `${browserOrigin}${path}`
}

function isErrorResponse(response: unknown): response is { error?: string; errors?: unknown } {
  return !!response && typeof response == 'object' && ('error' in response || 'errors' in response)
}

function authErrorFromResponse(response: { error?: string; errors?: unknown }): AuthError {
  if (response.error) {
    return { type: 'unknown', message: response.error }
  }
  return { type: 'unknown', message: 'Something went wrong. Please try again.' }
}

function toAuthError(error: Error): AuthError {
  return { type: 'unknown', message: error.message }
}
